import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Clock, AlertTriangle, XCircle, TrendingUp, FileText, Bell } from "lucide-react";

interface StatsCardsProps {
  stats: {
    total: number;
    cumplidas: number;
    enProceso: number;
    pendientes: number;
    vencidas: number;
    proximasAVencer: number;
    porcentajeCumplimiento: number;
  };
  onFilterClick?: (filterType: 'all' | 'Cumplido' | 'En Proceso' | 'Pendiente' | 'Vencido' | 'ProximasVencer') => void;
  activeFilter?: string;
}

const StatsCards = ({ stats, onFilterClick, activeFilter = 'all' }: StatsCardsProps) => {
  const cards = [
    {
      title: "Cumplimiento Total",
      value: `${stats.porcentajeCumplimiento}%`,
      icon: TrendingUp,
      color: "text-environmental",
      bgColor: "bg-environmental/10",
      filter: 'all' as const
    },
    {
      title: "Obligaciones Totales",
      value: stats.total.toString(),
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10",
      filter: 'all' as const
    },
    {
      title: "Obligaciones Cumplidas",
      value: stats.cumplidas.toString(),
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-100",
      filter: 'Cumplido' as const
    },
    {
      title: "En Proceso",
      value: stats.enProceso.toString(),
      icon: Clock,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      filter: 'En Proceso' as const
    },
    {
      title: "Pendientes",
      value: stats.pendientes.toString(),
      icon: AlertTriangle,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      filter: 'Pendiente' as const
    },
    {
      title: "Próximas a Vencer",
      value: stats.proximasAVencer.toString(),
      icon: Bell,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      filter: 'ProximasVencer' as const
    },
    {
      title: "Vencidas",
      value: stats.vencidas.toString(),
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-100",
      filter: 'Vencido' as const
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
      {cards.map((card, index) => {
        const Icon = card.icon;
        const isActive = activeFilter === card.filter;
        const isClickable = card.filter !== 'all' || card.title === "Obligaciones Totales";
        
        return (
          <Card 
            key={index} 
            className={`transition-all ${
              isClickable ? 'cursor-pointer' : 'cursor-default'
            } ${
              isActive && isClickable
                ? 'ring-2 ring-orange-500 shadow-lg scale-105' 
                : isClickable
                ? 'hover:shadow-md hover:scale-102'
                : ''
            }`}
            onClick={() => isClickable && onFilterClick?.(card.filter)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-full ${card.bgColor}`}>
                <Icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${card.color}`}>
                {card.value}
              </div>
              {isActive && isClickable && (
                <p className="text-xs text-muted-foreground mt-1">Filtro activo</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StatsCards;